/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, Container, Grid2, Paper, Typography, CircularProgress } from '@mui/material';
import { withStyles } from '@mui/styles';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { COLORS } from '../../utils/Application_Constants';
import useQuery from '../../utils/useQuery';
import { config } from '../Components/Expense/ExpenseReviewConfig';
import ExpenseFiltersForm from '../Components/Expense/ExpenseFiltersForm';
import {
    getVaultChargesWithFilter,
    getVaultExpensesCSVWithFilter,
    getExpensesCSVWithFilter,
    getExpenseCategories,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import ExpenseTable from '../Components/Expense/ExpenseTable';
import Alert from '../Components/Alert';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import getCriteriaFromLocalStorage from '../Components/Expense/GetCriteriaFromLocalStorage';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import { checkFeatureFlag } from '../../components/GlobalComponents/FeatureFlagComponent/FeatureFlag';
import { ExpenseClassEnum } from '../Components/Expense/ExpenseClass.enum';

const styles = (theme) => ({
    PaperRoot: {
        padding: '1.5rem 2rem',
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
        marginTop: '2em',
        maxWidth: '1690px',
        margin: '0 auto',
    },
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '2.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
    },
    header: {
        fontWeight: '700',
        margin: 0,
    },
    paginationNumber: {
        color: COLORS.LT_MIDNIGHT50,
        fontWeight: '600',
        fontSize: '14px',
        [theme.breakpoints.down('md')]: {
            marginBottom: '15px',
        },
    },
    paginationSpace: {
        paddingTop: '2em',
    },
    outlinedPageButton: {
        color: COLORS.LT_MIDNIGHT75,
        background: 'white',
    },
    tableContainer: {
        display: 'block',
    },
});

const VaultReview = (props) => {
    const { classes } = props;
    const history = useHistory();
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser } = useContext(AdminAuthContext);
    const isViewOnly = !(isExpenseSystemAdministratorUser || isExpenseAdminUser);
    const [expenses, setExpenses] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const query = useQuery();
    const [criteria, setCriteria] = useState(getCriteriaFromLocalStorage(ExpenseClassEnum.Vault));
    const defaultPage = useMemo(() => {
        const sessionPage = sessionStorage.getItem('vaultChargesPage');
        const queryPage = query.get('page');

        if (queryPage) return Number(queryPage);
        if (sessionPage) return Number(sessionPage);

        return 1;
    }, [query]);
    const [page, setPage] = useState(defaultPage);
    const [pages, setPages] = useState(0);
    const [sortBy, setSortBy] = useState(criteria ? criteria.sortBy : 'division');
    const [sortDirection, setSortDirection] = useState(criteria ? criteria.sortDirection : 'asc');
    const [limit, setLimit] = useState(50);
    const [alert, setAlert] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const getOffset = page === 1 ? 0 : (page - 1) * limit;
    const [offset, setOffset] = useState(getOffset);
    const [isSorting, setIsSorting] = useState(false);
    const [expenseCategories, setExpenseCategories] = useState(null);
    const [isNCREFFEnabled, setIsNCREFFEnabled] = useState(false); // Non-Clinician Reimbursement Expense Feature Flag check
    const defaultFormValues = {
        Statuses: criteria ? criteria.statuses : [],
        Divisions: criteria ? criteria.divisions : [],
        'Expense Types': criteria && criteria.expenseTypes ? criteria.expenseTypes : [],
        'Bill Types': criteria && criteria.billTypes ? criteria.billTypes : [],
        Sources: criteria && criteria.sources ? criteria.sources : [],
        'Needs Review Reasons':
            criteria && criteria.needsReviewReasons ? criteria.needsReviewReasons : [],
        'Has Receipt': criteria?.hasReceipt || [],
        'Merchant States': criteria && criteria.merchantStates ? criteria.merchantStates : [],
        StartStartDate: criteria && criteria.startStartDate ? criteria.startStartDate : null,
        StartEndDate: criteria && criteria.startEndDate ? criteria.startEndDate : null,
        EndStartDate: criteria && criteria.endStartDate ? criteria.endStartDate : null,
        EndEndDate: criteria && criteria.endEndDate ? criteria.endEndDate : null,
        PostStartDate: criteria && criteria.postStartDate ? criteria.postStartDate : null,
        PostEndDate: criteria && criteria.postEndDate ? criteria.postEndDate : null,
        PeriodEndStart: criteria && criteria.periodEndStart ? criteria.periodEndStart : null,
        PeriodEndEnd: criteria && criteria.periodEndEnd ? criteria.periodEndEnd : null,
        ImportedStart: criteria && criteria.importedStart ? criteria.importedStart : null,
        ImportedEnd: criteria && criteria.importedEnd ? criteria.importedEnd : null,
        PayDateStart: criteria && criteria.payDateStart ? criteria.payDateStart : null,
        PayDateEnd: criteria && criteria.payDateEnd ? criteria.payDateEnd : null,
        TransactionStartDate:
            criteria && criteria.transactionStartDate ? criteria.transactionStartDate : null,
        TransactionEndDate:
            criteria && criteria.transactionEndDate ? criteria.transactionEndDate : null,
        SubmittedStartDate:
            criteria && criteria.submittedStartDate ? criteria.submittedStartDate : null,
        SubmittedEndDate: criteria && criteria.submittedEndDate ? criteria.submittedEndDate : null,
    };
    const methods = useForm({ defaultValues: defaultFormValues });
    const { handleSubmit, reset, getValues, setValue, watch } = methods;
    const watchForValues = watch();
    const [totalNumberExpense, setTotalNumberExpense] = useState(getValues('totalNumberExpense'));

    const getNewSortValues = (sortKey) => {
        let newSortBy = sortBy;
        let newSortDirection = sortDirection;
        if (sortKey !== sortBy) {
            newSortBy = sortKey;
            newSortDirection = 'asc';
            setSortBy(sortKey);
            setSortDirection(newSortDirection);
        } else {
            newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            newSortBy = newSortDirection === 'asc' && sortDirection === 'desc' ? null : sortKey;
            if (newSortBy !== sortKey) setSortBy(newSortBy);
            setSortDirection(newSortDirection);
        }

        return { newSortBy, newSortDirection };
    };

    const [downloading, setDownloading] = useState(false);

    const fetchAndDownloadCSV = async (request, filePrefix) => {
        try {
            if (totalNumberExpense > 150000) {
                throw new Error('Filtered results must be 150,000 expenses or less.');
            }
            const response = await getExpensesCSVWithFilter(request);
            if (response.status === 404)
                throw new Error(`No data found to download ${filePrefix} file`);
            if (response.status !== 200) throw new Error('Failed to download file');

            // Format timestamp and create filename
            const timestamp = moment().format('YYYYMMDD_HHmm');
            const fileName = `${filePrefix}_${timestamp}.csv`;

            // Convert response data to CSV and trigger download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(`Error downloading ${filePrefix} file:`, error);
            setAlert({
                message: formatErrorMessage(error),
                type: 'error',
            });
        }
    };

    const downloadVaultCSV = async (request) => {
        try {
            if (totalNumberExpense > 150000) {
                throw new Error('Filtered results must be 150,000 expenses or less.');
            }

            const response = await getVaultExpensesCSVWithFilter(request);

            if (response.status === 404) {
                setAlert({
                    message:
                        response.message ||
                        'No vault expenses found matching the specified criteria.',
                    type: 'warning',
                });
                return;
            }

            if (response.status !== 200) {
                throw new Error('Failed to download Vault CSV');
            }

            const timestamp = moment().format('YYYYMMDD_HHmm');
            const fileName = `Vault_Expenses_${timestamp}.csv`;

            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading Vault CSV file:', error);
            const requestId = error?.requestId;
            const message = formatErrorMessage(error);
            setAlert({
                message: requestId ? `${message} (Request ID: ${requestId})` : message,
                type: 'error',
            });
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const filterValues = getCriteriaFromLocalStorage(ExpenseClassEnum.Vault);
            const allReimbursement = ['Reimbursement (Associate)', 'Reimbursement (Clinician)'];
            const allCC = ['Corpay', 'Conferma', 'Travel Inc'];
            const ReimbursementExpenseFile =
                !filterValues.sources ||
                filterValues.sources.length === 0 ||
                filterValues.sources.some((source) => allReimbursement.includes(source));
            const CreditCardExpenseFile =
                !filterValues.sources ||
                filterValues.sources.length === 0 ||
                filterValues.sources.some((source) => allCC.includes(source));
            const request = {
                offset: 0,
                sortBy,
                sortDirection,
                limit: 150000,
                statuses: filterValues ? filterValues.statuses : [],
                divisions: filterValues ? filterValues.divisions : [],
                transactionStartDate:
                    filterValues && filterValues.transactionStartDate
                        ? filterValues.transactionStartDate
                        : null,
                transactionEndDate:
                    filterValues && filterValues.transactionEndDate
                        ? filterValues.transactionEndDate
                        : null,
                expenseTypes:
                    filterValues && filterValues.expenseTypes ? filterValues.expenseTypes : [],
                sources: filterValues && filterValues.sources ? filterValues.sources : [],
                needsReviewReasons:
                    filterValues && filterValues.needsReviewReasons
                        ? filterValues.needsReviewReasons
                        : [],
                hasReceipt: filterValues?.hasReceipt || [],
                billTypes: filterValues && filterValues.billTypes ? filterValues.billTypes : [],
                startStartDate:
                    filterValues && filterValues.startStartDate
                        ? filterValues.startStartDate
                        : null,
                startEndDate:
                    filterValues && filterValues.startEndDate ? filterValues.startEndDate : null,
                endStartDate:
                    filterValues && filterValues.endStartDate ? filterValues.endStartDate : null,
                endEndDate:
                    filterValues && filterValues.endEndDate ? filterValues.endEndDate : null,
                postStartDate:
                    filterValues && filterValues.postStartDate ? filterValues.postStartDate : null,
                postEndDate:
                    filterValues && filterValues.postEndDate ? filterValues.postEndDate : null,
                periodEndStart:
                    filterValues && filterValues.periodEndStart
                        ? filterValues.periodEndStart
                        : null,
                periodEndEnd:
                    filterValues && filterValues.periodEndEnd ? filterValues.periodEndEnd : null,
                payDateStart:
                    filterValues && filterValues.payDateStart ? filterValues.payDateStart : null,
                payDateEnd:
                    filterValues && filterValues.payDateEnd ? filterValues.payDateEnd : null,
                submittedStartDate:
                    filterValues && filterValues.submittedStartDate
                        ? filterValues.submittedStartDate
                        : null,
                submittedEndDate:
                    filterValues && filterValues.submittedEndDate
                        ? filterValues.submittedEndDate
                        : null,
                merchantStates:
                    filterValues && filterValues.merchantStates ? filterValues.merchantStates : [],
                search: filterValues.search,
                searchKey:
                    filterValues && filterValues.searchKey
                        ? filterValues.searchKey
                        : config.searchKeys[0],
            };

            const downloadTasks = [];

            if (ReimbursementExpenseFile) {
                const reimbursementRequest = {
                    ...request,
                    sources:
                        !filterValues.sources || filterValues.sources.length === 0
                            ? allReimbursement
                            : filterValues.sources.filter((source) =>
                                  allReimbursement.includes(source)
                              ),
                };
                downloadTasks.push(
                    fetchAndDownloadCSV(reimbursementRequest, 'Reimbursement_Expenses')
                );
            }

            if (CreditCardExpenseFile && isNCREFFEnabled) {
                const creditCardRequest = {
                    ...request,
                    sources:
                        !filterValues.sources || filterValues.sources.length === 0
                            ? allCC
                            : filterValues.sources.filter((source) => allCC.includes(source)),
                };
                downloadTasks.push(fetchAndDownloadCSV(creditCardRequest, 'CreditCard_Expenses'));
            }

            // Execute both requests concurrently
            await Promise.allSettled(downloadTasks);
        } catch (error) {
            console.error('Error downloading file:', error);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadVaultCSV = async () => {
        setDownloading(true);
        try {
            const filterValues = getCriteriaFromLocalStorage(ExpenseClassEnum.Vault);
            const request = {
                offset: 0,
                sortBy,
                sortDirection,
                limit: 150000,
                statuses: filterValues ? filterValues.statuses : [],
                divisions: filterValues ? filterValues.divisions : [],
                transactionStartDate:
                    filterValues && filterValues.transactionStartDate
                        ? filterValues.transactionStartDate
                        : null,
                transactionEndDate:
                    filterValues && filterValues.transactionEndDate
                        ? filterValues.transactionEndDate
                        : null,
                expenseTypes:
                    filterValues && filterValues.expenseTypes ? filterValues.expenseTypes : [],
                sources: filterValues && filterValues.sources ? filterValues.sources : [],
                needsReviewReasons:
                    filterValues && filterValues.needsReviewReasons
                        ? filterValues.needsReviewReasons
                        : [],
                hasReceipt: filterValues?.hasReceipt || [],
                billTypes: filterValues && filterValues.billTypes ? filterValues.billTypes : [],
                startStartDate:
                    filterValues && filterValues.startStartDate
                        ? filterValues.startStartDate
                        : null,
                startEndDate:
                    filterValues && filterValues.startEndDate ? filterValues.startEndDate : null,
                endStartDate:
                    filterValues && filterValues.endStartDate ? filterValues.endStartDate : null,
                endEndDate:
                    filterValues && filterValues.endEndDate ? filterValues.endEndDate : null,
                postStartDate:
                    filterValues && filterValues.postStartDate ? filterValues.postStartDate : null,
                postEndDate:
                    filterValues && filterValues.postEndDate ? filterValues.postEndDate : null,
                periodEndStart:
                    filterValues && filterValues.periodEndStart
                        ? filterValues.periodEndStart
                        : null,
                periodEndEnd:
                    filterValues && filterValues.periodEndEnd ? filterValues.periodEndEnd : null,
                payDateStart:
                    filterValues && filterValues.payDateStart ? filterValues.payDateStart : null,
                payDateEnd:
                    filterValues && filterValues.payDateEnd ? filterValues.payDateEnd : null,
                submittedStartDate:
                    filterValues && filterValues.submittedStartDate
                        ? filterValues.submittedStartDate
                        : null,
                submittedEndDate:
                    filterValues && filterValues.submittedEndDate
                        ? filterValues.submittedEndDate
                        : null,
                merchantStates:
                    filterValues && filterValues.merchantStates ? filterValues.merchantStates : [],
                search: filterValues.search,
                importedStart:
                    filterValues && filterValues.importedStart ? filterValues.importedStart : null,
                importedEnd:
                    filterValues && filterValues.importedEnd ? filterValues.importedEnd : null,
                searchKey:
                    filterValues && filterValues.searchKey
                        ? filterValues.searchKey
                        : config.searchKeys[0],
            };
            const divisionNames = divisions
                .filter(
                    (division) =>
                        request.divisions && request.divisions.includes(division.description)
                )
                .map((division) => division.name);

            const selectedExpenseTypes = [];
            expenseCategories?.forEach((category) => {
                category.types.forEach((type) => {
                    if (request.expenseTypes?.includes(type.description)) {
                        selectedExpenseTypes.push(type.name);
                    }
                });
            });

            const updatedRequest = {
                ...request,
                divisions: divisionNames,
                expenseTypes: selectedExpenseTypes,
            };

            await downloadVaultCSV(updatedRequest);
        } catch (error) {
            console.error('Error preparing Vault CSV download:', error);
        } finally {
            setDownloading(false);
        }
    };

    const fetchData = async (request) => {
        localStorage.setItem('vaultChargesCriteria', JSON.stringify(request));

        // Map selected descriptions to api names
        const divisionNames = divisions
            .filter((div) => request.divisions && request.divisions.includes(div.description))
            .map((div) => div.name);
        const selectedTypeNames = [];
        expenseCategories.forEach((category) => {
            category.types.forEach((type) => {
                if (request.expenseTypes && request.expenseTypes.includes(type.description)) {
                    selectedTypeNames.push(type.name);
                }
            });
        });

        const updatedRequest = {
            ...request,
            divisions: divisionNames,
            expenseTypes: selectedTypeNames,
        };

        try {
            let response = await getVaultChargesWithFilter(updatedRequest);
            setTotalNumberExpense(response.totalCount);
            setExpenses(response.data);
            let aLastPage = Math.ceil(response.totalCount === 0 ? 0 : response.totalCount / limit);
            setPages(aLastPage);
            if (page > aLastPage && page !== 1) {
                if (request.offset === 0) {
                    setPage(1); // Ensure it's set to 1 when filtering/searching
                } else {
                    const newPage = aLastPage === 0 ? 1 : aLastPage;
                    if (page !== newPage) {
                        setPage(newPage);
                        const newOffset = newPage === 1 ? 0 : (newPage - 1) * limit;
                        setOffset(newOffset);
                        request.offset = newOffset;

                        // Only re-fetch if we had to adjust the page
                        response = await getVaultChargesWithFilter(request);
                        aLastPage = Math.ceil(
                            response.totalCount === 0 ? 0 : response.totalCount / limit
                        );
                        setPages(aLastPage);
                        setTotalNumberExpense(response.totalCount);
                        setExpenses(response.data.slice(0, limit));
                    }
                }
            }
            setIsSorting(false);
        } catch (error) {
            const message = formatErrorMessage(error);
            const code = error.statusCode;
            if (code === 404) {
                setExpenses([]);
                setTotalNumberExpense(0);
                setPages(0);
            } else {
                setAlert({
                    message,
                    type: 'error',
                });
            }
        } finally {
            setIsLoading(false);

            reset({
                Statuses: request?.statuses ? request?.statuses : [],
                Divisions: request?.divisions,
                'Expense Types': request?.expenseTypes,
                'Bill Types': request?.billTypes,
                Sources: request?.sources,
                'Needs Review Reasons': request?.needsReviewReasons,
                'Has Receipt': request?.hasReceipt,
                'Merchant States': request?.merchantStates,
                StartStartDate: request?.startStartDate,
                StartEndDate: request?.startEndDate,
                EndStartDate: request?.endStartDate,
                EndEndDate: request?.endEndDate,
                PostStartDate: request?.postStartDate,
                PostEndDate: request?.postEndDate,
                PeriodEndStart: request?.periodEndStart,
                PeriodEndEnd: request?.periodEndEnd,
                TransactionStartDate: request?.transactionStartDate,
                TransactionEndDate: request?.transactionEndDate,
                PayDateStart: request?.payDateStart,
                PayDateEnd: request?.payDateEnd,
                SubmittedStartDate: request?.submittedStartDate,
                SubmittedEndDate: request?.submittedEndDate,
            });
        }
    };

    const handleSort = async (sortKey) => {
        setIsSorting(true);
        setIsLoading(true);
        const { newSortBy, newSortDirection } = getNewSortValues(sortKey, sortBy, sortDirection);

        const currentCriteria = getCriteriaFromLocalStorage(ExpenseClassEnum.Vault);

        currentCriteria.sortBy = newSortBy;
        currentCriteria.sortDirection = newSortDirection;
        setCriteria(currentCriteria);
        await fetchData(currentCriteria);
    };

    const getData = async () => {
        setIsLoading(true);

        const ncreff = await checkFeatureFlag('NonClinicianReimbursementExpenseFeatures', false);
        setIsNCREFFEnabled(ncreff);

        const filterValues = getCriteriaFromLocalStorage(ExpenseClassEnum.Vault);
        const request = {
            expenseClass: ExpenseClassEnum.Vault,
            offset,
            sortBy,
            sortDirection,
            limit,
            statuses: defaultFormValues.Statuses,
            divisions: defaultFormValues.Divisions,
            transactionStartDate: defaultFormValues.TransactionStartDate,
            transactionEndDate: defaultFormValues.TransactionEndDate,
            expenseTypes: defaultFormValues['Expense Types'],
            sources: defaultFormValues.Sources,
            needsReviewReasons: defaultFormValues['Needs Review Reasons'],
            hasReceipt: defaultFormValues['Has Receipt'],
            billTypes: defaultFormValues['Bill Types'],
            startStartDate: defaultFormValues.StartStartDate,
            startEndDate: defaultFormValues.StartEndDate,
            endStartDate: defaultFormValues.EndStartDate,
            endEndDate: defaultFormValues.EndEndDate,
            postStartDate: defaultFormValues.PostStartDate,
            postEndDate: defaultFormValues.PostEndDate,
            periodEndStart: defaultFormValues.PeriodEndStart,
            periodEndEnd: defaultFormValues.PeriodEndEnd,
            payDateStart: defaultFormValues.PayDateStart,
            payDateEnd: defaultFormValues.PayDateEnd,
            submittedStartDate: defaultFormValues.SubmittedStartDate,
            submittedEndDate: defaultFormValues.SubmittedEndDate,
            importedStart: defaultFormValues.ImportedStart,
            importedEnd: defaultFormValues.ImportedEnd,
            search: filterValues ? filterValues.search : '',
            searchKey:
                filterValues && filterValues.searchKey
                    ? filterValues.searchKey
                    : config.searchKeys[0],
        };

        const divisionNames = request?.divisions?.map((div) => div.replace(/[\s-]/g, ''));
        const selectedTypeNames = request?.expenseTypes?.map((div) => div.replace(/[\s\-\/]/g, ''));
        const updatedRequest = {
            ...request,
            divisions: divisionNames,
            expenseTypes: selectedTypeNames,
        };
        await Promise.all([
            getVaultChargesWithFilter(updatedRequest),
            getExpenseCategories('Vault'),
            getDivisions(false, true),
        ])
            .then(async ([response, cats, divs]) => {
                setTotalNumberExpense(response.totalCount);
                setExpenses(response.data.slice(0, limit));
                setDivisions(divs);
                setExpenseCategories(cats);
                let aLastPage = Math.ceil(
                    response.totalCount === 0 ? 0 : response.totalCount / limit
                );
                setPages(aLastPage);
                if (page > aLastPage && page !== 1) {
                    if (request.offset === 0) {
                        setPage(1); // Ensure it's set to 1 when filtering/searching
                    } else {
                        const newPage = aLastPage === 0 ? 1 : aLastPage;
                        if (page !== newPage) {
                            setPage(newPage);
                            const newOffset = newPage === 1 ? 0 : (newPage - 1) * limit;
                            setOffset(newOffset);
                            request.offset = newOffset;

                            // Only re-fetch if we had to adjust the page
                            const filteredExpenses = await getVaultChargesWithFilter(request);
                            aLastPage = Math.ceil(
                                filteredExpenses.totalCount === 0
                                    ? 0
                                    : filteredExpenses.totalCount / limit
                            );
                            setPages(aLastPage);
                            setTotalNumberExpense(filteredExpenses.totalCount);
                            setExpenses(filteredExpenses.data.slice(0, limit));
                        }
                    }
                }
                localStorage.setItem('vaultChargesCriteria', JSON.stringify(request));
            })
            .catch((error) => {
                const message = formatErrorMessage(error);
                if (message === 'Internal Server Error') {
                    setExpenses([]);
                } else {
                    setAlert({
                        message,
                        type: 'error',
                    });
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handlePageChange = async (e, newPage) => {
        setIsLoading(true);
        const newOffset = newPage === 1 ? 0 : (newPage - 1) * limit;
        setOffset(newOffset);
        setPage(newPage);

        const currentCriteria = getCriteriaFromLocalStorage(ExpenseClassEnum.Vault);

        currentCriteria.offset = newOffset;
        currentCriteria.page = newPage;
        setCriteria(currentCriteria);
        await fetchData(currentCriteria);
    };

    const handleUpdateExpenses = (updatedExpenses) => {
        setExpenses(updatedExpenses);
    };

    const refreshData = async () => {
        setIsLoading(true);
        const currentCriteria = getCriteriaFromLocalStorage(ExpenseClassEnum.Vault);
        await fetchData(currentCriteria);
    };

    const renderPagination = () => (
        <Grid2
            container
            justifyContent="space-between"
            alignItems="center"
            className={classes.paginationSpace}
        >
            <Grid2 item>
                <div className={classes.paginationNumber}>
                    {`Page ${page} of ${!expenses ? 0 : pages}`}
                </div>
            </Grid2>
            <Grid2 item>
                {pages > 1 && (
                    <Pagination
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        page={page}
                        count={!expenses ? 0 : pages}
                        onChange={(e, newPage) => handlePageChange(e, newPage)}
                        renderItem={(item) => {
                            const hidden =
                                (item.type === 'previous' || item.type === 'next') && item.disabled;
                            if (hidden) return;
                            return (
                                <PaginationItem
                                    {...item}
                                    classes={{ outlined: classes.outlinedPageButton }}
                                />
                            );
                        }}
                    />
                )}
            </Grid2>
        </Grid2>
    );

    const renderFilters = () => (
        <FormProvider {...methods}>
            <ExpenseFiltersForm
                expenseClass={ExpenseClassEnum.Vault}
                criteria={criteria}
                fetchData={fetchData}
                setIsLoading={setIsLoading}
                setOffset={setOffset}
                setPage={setPage}
                limit={limit}
                sortBy={sortBy}
                sortDirection={sortDirection}
                expenseCategories={expenseCategories}
                availableDivisions={divisions}
                pendingCCReview={false}
                vaultReview
                isLoading={isLoading}
            />
        </FormProvider>
    );

    const renderExpenseTable = () => (
        <Paper classes={{ root: classes.PaperRoot }}>
            <ExpenseTable
                config={config}
                expenses={expenses}
                sortBy={sortBy}
                isLoading={isLoading}
                sortDirection={sortDirection}
                handleSort={handleSort}
                limit={limit}
                vaultChargesReview
                onUpdateExpenses={handleUpdateExpenses}
                onRefreshData={refreshData}
            />
        </Paper>
    );

    useEffect(() => {
        history.replace(`/expenses/vault/review?page=${page}`);
        sessionStorage.setItem('vaultChargesPage', Number(page));
        getData();
    }, []);

    useEffect(() => {
        history.replace(`/expenses/vault/review?page=${page}`);
        sessionStorage.setItem('vaultChargesPage', Number(page));
    }, [page]);

    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <Grid2>
                <Grid2 container justifyContent="space-between" spacing={2}>
                    <Grid2 item>
                        <Typography variant="h3" component="h3" className={classes.header}>
                            Vault Charges ({totalNumberExpense || 0})
                        </Typography>
                    </Grid2>
                    <Grid2 item sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleDownloadVaultCSV}
                            disabled={downloading}
                            sx={{ minWidth: '220px', display: 'flex', justifyContent: 'center' }}
                        >
                            {downloading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Download Vault CSV'
                            )}
                        </Button>
                    </Grid2>
                </Grid2>

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit()}>
                        <Grid2>{renderFilters()}</Grid2>
                        {pages > 0 && renderPagination()}
                        <Grid2>{renderExpenseTable()}</Grid2>
                    </form>
                </FormProvider>

                {pages > 0 && renderPagination()}
            </Grid2>
            <Alert alert={alert} setAlert={setAlert} />
        </Container>
    );
};

export default withStyles(styles)(VaultReview);
